import {
  Alert,
  Button,
  FormControl,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import {
  signinMember,
  resetSigninState,
} from "../../features/signinMemberSlice";
import { useLocation, useNavigate } from "react-router-dom";
import ForgotPassword from "./ForgotPassword";

const SignInComponent = () => {
  const waryLogo = require("../../assets/img/wary-logo.png");
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  // Route the guards bounced us off of, so signin returns there instead of
  // always dumping the user on the dashboard.
  const fromQuery = new URLSearchParams(location.search).get("from");
  const redirectTo =
    (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ||
    (fromQuery && fromQuery.startsWith("/") && !fromQuery.startsWith("//") ? fromQuery : null) ||
    "/dashboard";
  const { isSignedIn } = useSelector((state: RootState) => state.signinMember);
  const [open, setOpen] = React.useState(false);
  const [openAlert, setOpenAlert] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");

  useEffect(() => {
    if (isSignedIn) {
      navigate(redirectTo, { replace: true });
    }
  }, [isSignedIn, navigate, redirectTo]);

  const signinSubmit = (values: { email?: string | null; password?: string | null }) => {
    dispatch(signinMember(values))
      .then((response: any) => {
        console.log("Response:", response);
        if (response.meta.requestStatus === "rejected") {
          const message =
            typeof response.payload === "string"
              ? response.payload
              : response.payload?.message || "Sign in failed";
          setOpenAlert(true);
          setAlertMessage(message);
          dispatch(resetSigninState());
        }
      })
      .catch((error: any) => {
        console.log("Error:", error);
      });
  };

  const { values, errors, handleBlur, handleChange, handleSubmit } = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Required"),
      password: Yup.string().required("Required"),
    }),
    onSubmit: (values) => {
      signinSubmit(values);
    },
  });

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Grid container direction={"column"}>
      <Grid
        size={{ xs: "grow", md: 4 }}
        offset={{ md: 4 }}
        style={{ marginTop: "2rem" }}
      >
        <Paper square={false} elevation={3} style={{ padding: "2rem" }}>
          <Grid
            display={"flex"}
            justifyContent={"center"}
            style={{ marginBottom: "2rem" }}
          >
            <img src={waryLogo} alt="WARY" width={200} />
          </Grid>
          <Typography variant="h4">Sign In</Typography>
          <Typography variant="subtitle1">
            Enter your credentials to sign in
          </Typography>
          {openAlert && (
            <Alert severity="error" style={{ margin: "1rem 0 1rem" }}>
              {alertMessage}
            </Alert>
          )}
          <form autoComplete="off" onSubmit={handleSubmit} noValidate>
            <Grid size={{ xs: 12, md: 12 }}>
              <FormControl fullWidth sx={{ m: 1 }} variant="standard">
                <TextField
                  id="email"
                  label="Email"
                  variant="standard"
                  name="email"
                  margin="normal"
                  value={values.email}
                  onBlur={(e) =>
                    handleBlur({
                      target: { name: "email", value: e.target.value },
                    })
                  }
                  onChange={(e) =>
                    handleChange({
                      target: { name: "email", value: e.target.value },
                    })
                  }
                  error={!!errors.email}
                  helperText={errors.email}
                  fullWidth
                  required
                />
              </FormControl>
              <FormControl fullWidth sx={{ m: 1 }} variant="standard">
                <TextField
                  name="password"
                  id="password"
                  label="Password"
                  type="password"
                  variant="standard"
                  margin="normal"
                  value={values.password}
                  onBlur={(e) =>
                    handleBlur({
                      target: { name: "password", value: e.target.value },
                    })
                  }
                  onChange={(e) =>
                    handleChange({
                      target: { name: "password", value: e.target.value },
                    })
                  }
                  helperText={errors.password}
                  error={!!errors.password}
                  fullWidth
                  required
                />
              </FormControl>
              <ForgotPassword open={open} handleClose={handleClose} />
              <Link
                component="button"
                type="button"
                onClick={handleClickOpen}
                variant="body2"
                sx={{ alignSelf: "center" }}
              >
                Forgot your password?
              </Link>
              <FormControl
                fullWidth
                sx={{ m: 1 }}
                variant="standard"
                style={{ marginTop: "2rem" }}
              >
                <Stack direction="row" spacing={2} justifyContent={"flex-end"}>
                  <Button size="large" type="reset">
                    Cancel
                  </Button>
                  <Button variant="contained" size="large" type="submit">
                    Sign In
                  </Button>
                </Stack>
              </FormControl>
            </Grid>
          </form>
        </Paper>
      </Grid>
    </Grid>
  );
};

SignInComponent.propTypes = {
  email: PropTypes.string,
  password: PropTypes.string,
};

export { SignInComponent };
